import { useEffect, useRef, useState } from "react";
import { Reorder } from "framer-motion";
import { Plus, Pencil, Trash2, Copy, Loader2, Star, EyeOff, GripVertical } from "lucide-react";
import { adminApi, imageUrl } from "../../lib/adminApi";
import { ProjectForm } from "./ProjectForm";
import { ConfirmModal } from "./ConfirmModal";

export const ProjectsPanel = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // null | "new" | project
  const [confirmDel, setConfirmDel] = useState(null); // project id pending deletion
  const [deleting, setDeleting] = useState(false);
  const orderRef = useRef([]);

  const load = async () => {
    setLoading(true);
    try {
      const list = await adminApi.listProjects();
      setProjects(list);
      orderRef.current = list.map((p) => p.id);
    } catch (e) {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleReorder = (newList) => {
    setProjects(newList);
    orderRef.current = newList.map((p) => p.id);
  };

  const persistOrder = () => {
    if (orderRef.current.length) adminApi.reorderProjects(orderRef.current).catch(() => {});
  };

  const remove = async () => {
    if (!confirmDel) return;
    setDeleting(true);
    try {
      await adminApi.deleteProject(confirmDel);
      setConfirmDel(null);
      await load();
    } finally {
      setDeleting(false);
    }
  };

  const duplicate = async (id) => {
    setBusy(true);
    try {
      await adminApi.duplicateProject(id);
      await load();
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <ProjectForm
        initial={editing === "new" ? null : editing}
        onCancel={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          load();
        }}
      />
    );
  }

  return (
    <div data-testid="projects-panel">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-display text-2xl font-medium">Réalisations</h2>
        <button
          onClick={() => setEditing("new")}
          data-testid="project-new-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-accent-yellow text-ink text-sm font-semibold hover:bg-white transition-colors"
        >
          <Plus size={16} /> Nouveau projet
        </button>
      </div>
      <p className="text-xs text-white/35 mb-6">Glissez-déposez les projets pour définir leur ordre d'affichage sur le site.</p>

      {loading ? (
        <div className="flex items-center gap-2 text-white/50 text-sm"><Loader2 size={16} className="animate-spin" /> Chargement…</div>
      ) : projects.length === 0 ? (
        <p className="text-white/45 text-sm" data-testid="projects-empty">Aucun projet pour le moment. Cliquez sur « Nouveau projet ».</p>
      ) : (
        <Reorder.Group axis="y" values={projects} onReorder={handleReorder} className="grid gap-4">
          {projects.map((p) => (
            <Reorder.Item
              key={p.id}
              value={p}
              onDragEnd={persistOrder}
              data-testid={`admin-project-${p.id}`}
              className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4 cursor-grab active:cursor-grabbing"
            >
              <GripVertical size={18} className="text-white/25 shrink-0" />
              <div className="w-20 h-16 rounded-lg overflow-hidden bg-white/[0.05] shrink-0 flex items-center justify-center">
                {p.image_id ? (
                  <img src={imageUrl(p.image_id)} alt="" className="w-full h-full object-cover pointer-events-none" />
                ) : (
                  <span className="text-[10px] text-white/25">—</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium truncate">{p.title || "Sans titre"}</p>
                  {p.featured && <Star size={13} className="text-accent-yellow shrink-0" fill="currentColor" />}
                  {!p.published && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-white/40"><EyeOff size={11} /> Brouillon</span>
                  )}
                </div>
                <p className="text-sm text-white/45 truncate">{[p.category?.fr, p.location].filter(Boolean).join(" · ") || "—"}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => duplicate(p.id)} disabled={busy} data-testid={`project-duplicate-${p.id}`} title="Dupliquer" className="p-2.5 rounded-lg border border-white/12 text-white/70 hover:text-accent-yellow hover:border-accent-yellow transition-colors disabled:opacity-50"><Copy size={15} /></button>
                <button onClick={() => setEditing(p)} data-testid={`project-edit-${p.id}`} title="Modifier" className="p-2.5 rounded-lg border border-white/12 text-white/70 hover:text-accent-yellow hover:border-accent-yellow transition-colors"><Pencil size={15} /></button>
                <button onClick={() => setConfirmDel(p.id)} data-testid={`project-delete-${p.id}`} title="Supprimer" className="p-2.5 rounded-lg border border-white/12 text-white/70 hover:text-red-400 hover:border-red-400/50 transition-colors"><Trash2 size={15} /></button>
              </div>
            </Reorder.Item>
          ))}
        </Reorder.Group>
      )}

      <ConfirmModal
        open={!!confirmDel}
        title="Supprimer ce projet ?"
        message="Cette action est définitive et supprimera également son image."
        confirmLabel="Supprimer"
        loading={deleting}
        onConfirm={remove}
        onClose={() => setConfirmDel(null)}
      />
    </div>
  );
};
